"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FileSpreadsheet, FileText } from "lucide-react";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";

interface SubscriptionsDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      activeCount: number;
      usedToday: number;
      totalRemaining: number;
      expiringSoon: number;
    };
    trend: Array<{
      date: string;
      subscriptions: number;
      oneTime: number;
      total: number;
    }>;
    comparison: Array<{
      type: string;
      count: number;
      percentage: number;
    }>;
  };
}

export function SubscriptionsDashboard({ data }: SubscriptionsDashboardProps) {

  const getExportData = () => {
    return data.trend.map((item) => ({
      Date: item.date,
      Subscriptions: item.subscriptions,
      "One-Time": item.oneTime,
      Total: item.total,
    }));
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), "Subscriptions_Report", "Subscriptions");
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Subscriptions_Report", {
      title: "Subscriptions Report",
      subtitle: "Subscription usage trends and comparison",
      dateRange: data.dateRange,
    });
  };

  return (
    <div className="space-y-4">
      {/* Subscription Usage Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Active Subscriptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.summary.activeCount}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently active plans
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Used Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.summary.usedToday}
            </div>
            <p className="text-xs text-muted-foreground">
              Subscription services used
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Remaining Credits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.summary.totalRemaining}
            </div>
            <p className="text-xs text-muted-foreground">
              Total services remaining
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.summary.expiringSoon}
            </div>
            <p className="text-xs text-muted-foreground">
              Within next 30 days
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Subscription Usage Trend */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Daily Subscription Usage Trend</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="subscriptions"
                stroke="#0ea5e9"
                name="Subscriptions"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="oneTime"
                stroke="#8b5cf6"
                name="One-Time"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Subscription vs One-time Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription vs One-Time Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.comparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#0ea5e9" name="Count" />
              </BarChart>
            </ResponsiveContainer>

            <div className="grid gap-4 md:grid-cols-2">
              {data.comparison.map((item) => (
                <div key={item.type} className="p-4 border rounded-lg">
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    {item.type}
                  </div>
                  <div className="text-2xl font-bold">
                    {item.count}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {item.percentage}% of total
                  </div>
                </div>
              ))}
            </div>

            {data.comparison[0] && data.comparison[0].percentage < 30 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Insight:</strong> Subscriptions represent only{" "}
                  {data.comparison[0].percentage}% of total bookings. Consider
                  promoting subscription plans to increase recurring revenue.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

